import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ClipboardList, HeartPulse, Stethoscope } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";
import heroAsset from "@/assets/blog/pcos-metabolic-hero.png.asset.json";
import hazardAsset from "@/assets/blog/pcos-prediabetes-hazard.png.asset.json";
import screeningAsset from "@/assets/blog/pcos-ogtt-screening.png.asset.json";

const SLUG = "pcos-metabolic-health-prediabetes";
const URL = `https://truwe.in/blog/${SLUG}`;
const TITLE = "PCOS Is More Than an Irregular Period: Metabolic Health & Prediabetes | TruWe";

const FAQS = [
  {
    q: "Does PCOS mean I will get diabetes?",
    a: "No. In a long-term population study, women with PCOS had a 23% higher relative hazard of moving from normal glucose regulation to prediabetes. PCOS did not significantly predict a direct move to type 2 diabetes, and a relative hazard is not a personal prediction.",
  },
  {
    q: "What does a 23% higher hazard actually mean?",
    a: "It is a relative comparison between women with and without PCOS over the study period. It does not mean 23 out of 100 women with PCOS will develop prediabetes. Your own risk depends on age, family history, body composition, pregnancy history, sleep, activity, medicines and other metabolic conditions.",
  },
  {
    q: "My fasting sugar is normal. Do I still need screening?",
    a: "Discuss it with your clinician. A normal fasting glucose is reassuring but may not capture the whole metabolic picture in PCOS. The 2023 international guideline recommends assessing glycaemic status at diagnosis and reassessing every one to three years based on individual risk.",
  },
  {
    q: "Which test is most accurate in PCOS?",
    a: "The 75 g oral glucose tolerance test (OGTT) is recommended as the most accurate test for glycaemic assessment in PCOS, regardless of BMI. Fasting plasma glucose and/or HbA1c can be considered when an OGTT cannot be performed, while recognising they are less accurate in PCOS.",
  },
  {
    q: "I am lean. Is metabolic screening still relevant?",
    a: "Yes. PCOS-related glucose dysregulation is not limited to one body type, and the 2023 guideline recommends assessing glycaemic status in women with PCOS regardless of BMI.",
  },
] as const;

const ACTIONS = [
  "Build meals around protein, fibre and minimally processed foods rather than chasing a perfect “PCOS diet.”",
  "Include regular movement, with both aerobic activity and resistance training where appropriate.",
  "Protect sleep; poor sleep can make appetite and glucose regulation harder.",
  "Avoid treating supplements as substitutes for testing, nutrition, movement or prescribed care.",
  "Review your results with a qualified clinician instead of interpreting one number in isolation.",
] as const;

const GUIDELINE = [
  "Assess glycaemic status at diagnosis.",
  "Reassess every one to three years, depending on individual risk factors.",
  "Use a 75 g oral glucose tolerance test (OGTT) as the most accurate test for glycaemic assessment in PCOS, regardless of BMI.",
  "Consider fasting plasma glucose and/or HbA1c when an OGTT cannot be performed, recognising that they are less accurate in PCOS.",
] as const;

const SOURCES = [
  {
    label:
      "Kazemi Jaliseh H, Ramezani Tehrani F, Behboudi-Gandevani S, et al. Polycystic ovary syndrome is a risk factor for diabetes and prediabetes in middle-aged but not elderly women: a long-term population-based follow-up study. Fertility and Sterility. 2017;108(6):1078–1084.",
    url: "https://doi.org/10.1016/j.fertnstert.2017.09.004",
  },
  {
    label:
      "Teede HJ, Tay CT, Laven JJE, et al. Recommendations from the 2023 International Evidence-based Guideline for the Assessment and Management of Polycystic Ovary Syndrome. Fertility and Sterility. 2023.",
    url: "https://www.asrm.org/practice-guidance/practice-committee-documents/international-evidence-based-guideline-for-the-assessment-and-management-of-polycystic-ovary-syndrome-pcos/",
  },
  {
    label: "World Health Organization. Polycystic ovary syndrome. Updated 22 January 2026.",
    url: "https://www.who.int/news-room/fact-sheets/detail/polycystic-ovary-syndrome",
  },
] as const;

export const Route = createFileRoute("/blog/pcos-metabolic-health-prediabetes")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description:
        "Even with normal glucose, PCOS was linked to a 23% higher relative hazard of progressing to prediabetes. What that means, why insulin is involved, and how screening is recommended.",
      ogTitle: "PCOS Is More Than an Irregular Period",
      ogDescription:
        "Normal glucose today is not a lifetime guarantee. What a 23% higher hazard of prediabetes really means in PCOS.",
      headline: "PCOS Is More Than an Irregular Period",
      breadcrumbName: "PCOS & Metabolic Health",
      datePublished: "2026-08-28",
      dateModified: "2026-08-28",
      ogImage: `https://truwe.in${heroAsset.url}`,
      faqs: FAQS.map((f) => ({ q: f.q, a: f.a })),
    }),
});

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
      {children}
    </h2>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-base leading-relaxed text-foreground/85">{children}</p>;
}

function Bullets({ items }: { items: readonly string[] }) {
  return (
    <ul className="mt-4 space-y-2 text-base text-foreground/85">
      {items.map((i) => (
        <li key={i} className="flex gap-2.5">
          <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
          <span className="min-w-0">{i}</span>
        </li>
      ))}
    </ul>
  );
}

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Hormonal Health
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            PCOS is more than an irregular period
          </h1>
          <BlogByline date="2026-08-28" readingTime="8 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Even when your glucose looks normal, PCOS can be a reason to keep an eye on
            your metabolic health.
          </p>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset.url}
              alt="A woman sitting calmly, with soft molecular illustrations suggesting metabolic health in PCOS"
              width={1664}
              height={936}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <P>PCOS is often first noticed through the calendar.</P>
          <P>
            A late period. A missed period. Another month of wondering what your body is
            doing.
          </P>
          <P>
            But your cycle is not the only part of the picture. PCOS can also be linked
            with changes in how the body responds to insulin and manages glucose, sometimes
            before routine tests show prediabetes.
          </P>
          <P>
            A long-term population study offers a measured reason to stay informed: among
            women who began with normal glucose levels, those with PCOS had a{" "}
            <strong>23% higher relative hazard</strong> of progressing to prediabetes than
            women without PCOS.
          </P>
          <P>
            That figure does not mean diabetes is inevitable, and it does not predict what
            will happen to you personally. It suggests that, for some women with PCOS, the
            earliest metabolic change may be a shift from normal glucose regulation to
            prediabetes, making regular, appropriate screening worth discussing even when
            your latest result was normal.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>What the study actually found</H2>
          <P>
            Researchers followed a population-based cohort of 1,587 Iranian women across
            six follow-up assessments, roughly three years apart.
          </P>
          <figure className="mt-6 overflow-hidden rounded-3xl border border-border bg-card">
            <img
              src={hazardAsset.url}
              alt="Diagram: normal glucose to prediabetes with a 23% higher relative hazard observed in a longitudinal cohort"
              width={1244}
              height={1244}
              loading="lazy"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
              Association observed in a longitudinal cohort. A relative hazard compares
              groups; it is not an individual prediction.
            </figcaption>
          </figure>
          <P>They examined how women moved between three glycaemic states:</P>
          <Bullets
            items={["Normal glucose regulation", "Prediabetes", "Type 2 diabetes"]}
          />
          <P>
            PCOS was associated with a 23% higher hazard of moving from normal glucose
            regulation to prediabetes. However, PCOS did not significantly predict a direct
            transition from normal glucose to type 2 diabetes, or progression from
            prediabetes to type 2 diabetes.
          </P>
          <P>
            This is an important distinction. The study does not say that every woman with
            PCOS will develop diabetes. It suggests that the earliest metabolic shift, from
            normal glucose to prediabetes, may be where the added risk first becomes
            visible. And that is precisely why the finding matters: prediabetes is not the
            final chapter. It is an early signal.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-10 rounded-3xl border border-brand/20 bg-brand/[0.04] p-6 md:p-8">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
              <ClipboardList className="h-4 w-4" /> Read the number carefully
            </div>
            <h2 className="mt-2 font-display text-xl font-bold text-brand-deep md:text-2xl">
              What “23% higher hazard” does, and does not, mean
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground/85">
              It does not mean that 23 out of every 100 women with PCOS will develop
              prediabetes. It is a relative comparison between women with and without PCOS
              over the study period. Your personal risk still depends on factors such as
              age, family history, body composition, pregnancy history, sleep, activity,
              medicines and other metabolic conditions.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground/85">
              Because this was an observational study, it can identify an association but
              cannot prove that PCOS itself caused the progression. The cohort was also
              Iranian, so the exact risk estimate may not transfer unchanged to Indian
              women or other populations. Residual confounding remains possible.
            </p>
            <p className="mt-3 text-sm font-semibold text-brand-deep">
              The responsible conclusion is not panic. It is earlier attention.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Why PCOS and blood sugar are connected</H2>
          <P>
            Insulin is the signal that helps move glucose from the bloodstream into cells,
            where it can be used for energy.
          </P>
          <P>
            In many women with PCOS, the body becomes less responsive to that signal. The
            pancreas may compensate by producing more insulin. For a while, glucose can
            still appear normal, even while the body is working harder to keep it there.
          </P>
          <P>
            Higher insulin levels can also interact with ovarian hormone production and may
            contribute to features such as irregular ovulation and androgen excess. This is
            one reason PCOS is now understood as more than a reproductive condition. The
            World Health Organization describes it as a chronic metabolic condition that
            can continue beyond the reproductive years.
          </P>
          <P>
            Normal fasting glucose, therefore, is reassuring, but it is not always the
            whole metabolic picture.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>“But I am not overweight.”</H2>
          <P>PCOS-related metabolic risk is not reserved for one body type.</P>
          <P>
            Body weight can influence risk, but it is not a reliable permission slip to
            ignore screening. A woman can be lean, active and still have PCOS-related
            glucose dysregulation. The 2023 International Evidence-based PCOS Guideline
            recommends assessment of glycaemic status in women with PCOS regardless of BMI.
          </P>
          <P>
            You do not need to look metabolically unwell before taking metabolic health
            seriously.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>What should you discuss with your doctor?</H2>
          <P>
            If you have PCOS, ask when your glucose status was last assessed, not only
            whether your fasting sugar once looked normal.
          </P>
          <figure className="mt-6 overflow-hidden rounded-3xl border border-border bg-card">
            <img
              src={screeningAsset.url}
              alt="Screening reminder: reassess every one to three years; 75 g OGTT is the most accurate test in PCOS"
              width={1244}
              height={1244}
              loading="lazy"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
              Timing depends on individual risk. Your clinician decides the right schedule.
            </figcaption>
          </figure>
          <P>The international guideline recommends:</P>
          <Bullets items={GUIDELINE} />
          <div className="mt-6 rounded-2xl border border-brand/20 bg-card p-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
              <Stethoscope className="h-4 w-4" /> Also worth asking
            </div>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              An OGTT should also be discussed when planning pregnancy or fertility
              treatment if you have PCOS and do not already have diagnosed diabetes. Your
              clinician may suggest a different schedule based on family history, previous
              gestational diabetes, blood pressure, medicines, symptoms or earlier test
              results.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>What can you do before a report changes colour?</H2>
          <P>Do not wait for prediabetes to make your health feel urgent.</P>
          <P>Start with repeatable actions:</P>
          <Bullets items={ACTIONS} />
          <P>
            The goal is not punishment, restriction or fear. It is to reduce the metabolic
            workload your body has been carrying quietly.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>The real message of this research</H2>
          <P>PCOS is often noticed because the period is late.</P>
          <P>
            But the deeper opportunity is to act before metabolic health is late too.
          </P>
          <P>
            A normal glucose result is good news. It is not a lifetime guarantee. Regular
            screening gives you something far more useful than false reassurance: the
            chance to notice change early, while there is still room to respond.
          </P>
          <P>Your body is not failing you. It may simply be asking to be read as a whole.</P>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-12 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <HeartPulse className="h-4 w-4" /> Daily support, alongside your care
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Ova Balance
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              A once-daily sachet built around the widely studied 40:1 myo-inositol to
              D-chiro-inositol ratio, supported by berberine HCl, active folate and
              essential mineral cofactors. It is daily nutritional support, not a
              replacement for testing or prescribed care.
            </p>
            <Link
              to="/products/$handle"
              params={{ handle: "ova-balance" }}
              className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand px-6 text-sm font-semibold text-white transition hover:bg-brand-deep"
            >
              Ova Balance — 40:1 inositol for PCOS <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Frequently asked questions</H2>
          <div className="mt-5 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-base font-bold text-brand-deep md:text-lg">
                  {f.q}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="hormonal" />
          <ClusterLinks cluster="hormonal" />
          <AuthorityFlow cluster="hormonal" />
          <RelatedReading articles={relatedArticles(SLUG)} />
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Study note</H2>
          <P>
            The longitudinal findings discussed here come from a 2017 Fertility and
            Sterility paper, “Polycystic ovary syndrome is a risk factor for diabetes and
            prediabetes in middle-aged but not elderly women: a long-term population-based
            follow-up study” (DOI: 10.1016/j.fertnstert.2017.09.004). They should not be
            attributed to the September 2026 issue of Endocrinology, Diabetes &amp;
            Metabolism, which contains a separate population-based study on
            anti-Müllerian hormone and metabolic-hormonal profiles.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>References</H2>
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            {SOURCES.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  className="underline hover:text-brand"
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ol>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Medical disclaimer: this article is for education and is not a diagnosis or
            treatment plan. Speak with a qualified healthcare professional about testing and
            care appropriate to you.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
